import { useNavigate } from "react-router";
import { useCurrentUser } from "../../hooks/use-current-user";
import { ClientUserService, UserCache } from "../../services";
import BreadCrumbs from "../breadcrumbs";
import { Toolbar } from "primereact/toolbar";
import { useIcon, useNotificationCnt, useSettings, useTranslation } from "../../hooks";
import { FabrixIcon } from "../../common/icons";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { MenuItem, MenuItemCommandEvent } from "primereact/menuitem";
import { useEffect, useMemo, useRef, useState } from "react";
import { HeaderAction } from "fabrix/types";
import UserProfileComponent from "../user-profile/user-profile";
import { OverlayPanel } from "primereact/overlaypanel";
import { Badge } from "primereact/badge";
import NotificationPanel from "../notifications/notification-panel";
import { DraggableAssistant } from "../assistant/draggable-assistant";
import { DraggableAgent } from "../doc-agent/draggable-agent";
import { DefectDialog } from "../defects/defect-dialog";

type HeaderProps = {
  onMenuClick?: () => void;
};

/**
 * Renders the Fabrix application header containing breadcrumbs, profile controls, and notifications.
 * @returns Header toolbar composed of navigation and user utilities.
 */
export default function Header({ onMenuClick }: HeaderProps) {
  const _notCount = useNotificationCnt(false);
  const _currentUser = useCurrentUser();
  const settings = useSettings();
  const navigate = useNavigate();
  const translate = useTranslation();
  const icon = useIcon();
  const _menuRef = useRef<Menu>(null);
  const [_items, setItems] = useState<MenuItem[]>([]);
  const opProfile = useRef<OverlayPanel>(null);
  const opNotification = useRef<OverlayPanel>(null);
  const [assistantVisible, setAssistantVisible] = useState(false);
  const [_defectOpen, setDefectOpen] = useState(false);



  /**
   * Hides the profile overlay once the user initiates logout.
   */
  const handleLogout = (e: any) => {
    opProfile.current?.hide();
  };

  /**
   * Navigates to menu item routes when they define a path.
   * @param e PrimeReact menu command event triggered by selections.
   */
  const doNavigation = (e: MenuItemCommandEvent) => {
    let action = e.item as HeaderAction;
    if (action.path) navigate(action.path);
  };

  /**
   * Recursively decorates header actions with navigation commands and absolute URLs.
   * @param items Hierarchical header actions to process.
   */
  const linkProc = (items: HeaderAction[]) => {
    for (let i = 0; i < items.length; i++) {
      const a = { ...items[i] };
      if (a.path) {
        a.command = doNavigation;
        a.url = `${window.location.origin}${a.path}`;
        items[i] = a;
      }
      if (a.items && a.items.length > 0) {
        a.items.forEach((eachItem: HeaderAction) => {
          if (eachItem.path) {
            eachItem.url = `${window.location.origin}${eachItem.path}`;
          }
        });
        linkProc(a.items as HeaderAction[]);
      }
    }
  };

  useEffect(() => {
    // if (_currentUser?.roles?.indexOf("system-admin") !== -1)
    //     configureAdminMenu();
  }, [_currentUser]);

  /**
   * Prepares admin navigation items for system administrators.
   */
  const configureAdminMenu = () => {
    let menu = [
      {
        icon: icon(FabrixIcon.modules),
        path: "/security-management",
        label: translate("MODULES"),
      },
    ];
    linkProc(menu);
    setItems(menu);
  };

  const start = (
    <>
      <button type="button" className="header-menu-button" onClick={onMenuClick} aria-label="Toggle sidebar">
        <span className={icon(FabrixIcon.menu)} />
      </button>
      <span className={icon(FabrixIcon.fabrix)}></span>
      <BreadCrumbs />
    </>
  );

  const handleDebugClick = (event: any) => {
    const isModifiedClick = event.ctrlKey || event.metaKey;
    if (!isModifiedClick) {
      setDefectOpen(true)
    }
    let debugOptions = UserCache.debugOptions
    if (!debugOptions) {
      debugOptions = {
        logging: false
      }
    }
    debugOptions.logging = !debugOptions.logging
    UserCache.debugOptions = debugOptions
  }

  const end = (
    <div>
      {/* //Not sure about this menu at the moment. */}
      {/* <Menu model={_items} popup className="structure-menu" ref={_menuRef} />
             {hasRole(["system-admin"]) ?
                <Button icon={icon(FabrixIcon.menu)} onClick={(e) => _menuRef.current?.toggle(e)} className="admin-menu lim-button p-button-rounded p-button-text hover:surface-400" aria-label="Menu" />
                : undefined
            } */}

      <div className="header-block-right">
        <Button title={translate("LOG_DEFECT")} id="assistant-button" style={{ fontSize: 15, height: 30, width: 30, marginRight: 8 }} icon={icon(FabrixIcon.defect)} className="p-button-rounded p-button-text" onClick={handleDebugClick} />

        <Button title={ClientUserService.displayName(_currentUser)} id="profile-button" style={{ fontSize: 15, height: 30, width: 30 }} icon={icon(FabrixIcon.user)} className="p-button-rounded p-button-text" onClick={(e) => opProfile.current?.toggle(e, null)}></Button>
        <i onClick={(e) => opNotification.current?.toggle(e, null)} className={`${icon(FabrixIcon.notifications)} p-mr-4 p-overlay-badge notification-button`}>
          <Badge size="normal" id="notification-badge" className="notification-badge" severity="warning" value={_notCount}></Badge>
        </i>
      </div>

      <OverlayPanel ref={opProfile} showCloseIcon id="note-overlay_panel" className="profile" style={{ width: "300px" }}>
        <UserProfileComponent onLogout={handleLogout} onClose={() => opProfile.current?.hide()} />
      </OverlayPanel>

      <OverlayPanel ref={opNotification} showCloseIcon id="note_overlay_panel" className="notification-overlay">
        <NotificationPanel />
      </OverlayPanel>

      {/* <DraggableAssistant visible={assistantVisible} onHide={() => setAssistantVisible(false)} /> */}
      <DraggableAgent visible={assistantVisible} onHide={() => setAssistantVisible(false)} />
    </div>
  );

  const center = () => {
    let env = settings.systemEnvironment || "";
    if (env !== "") {
      return (<div className="fbx-header-environment">
        <span style={{ marginRight: 4 }} className={icon(FabrixIcon.circleInfo)}></span>
        <span>{env}</span>
      </div>);
    } else {
      return null;
    }
  }

  return (
    <div className="fabrix-header">
      <Toolbar start={start} center={center} end={end} className="nt-header" />
      <DefectDialog open={_defectOpen} onClose={() => setDefectOpen(false)} />
    </div>
  );
}
